import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bot, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from "@/lib/utils";
import { ChatbotConfig } from '@/services/chatbotService';

interface ConfigurationProgressProps {
  config: ChatbotConfig;
  onToggleEnabled: (enabled: boolean) => void;
}

export function ConfigurationProgress({ config, onToggleEnabled }: ConfigurationProgressProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });

  return (
    <div className="space-y-4">
      {/* Simple Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-6 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50/90 border border-slate-200/60 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-md",
              config.isEnabled ? "bg-emerald-500 shadow-emerald-500/25" : "bg-slate-400 shadow-slate-400/25"
            )}>
              {config.isEnabled ? (
                <CheckCircle className="w-6 h-6 text-white" />
              ) : (
                <Bot className="w-6 h-6 text-white" />
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Label className="text-lg font-semibold">
                {config.isEnabled ? t('chatbotActive') : t('activateChatbot')}
              </Label>
              <Badge 
                variant={config.isEnabled ? "default" : "secondary"}
                className={cn(
                  "shadow-sm border",
                  config.isEnabled ? "bg-emerald-100 text-emerald-800 border-emerald-200/50" : "border-slate-200/50"
                )}
              >
                {config.isEnabled ? t('live') : t('draft')}
              </Badge>
            </div>
            <p className="text-sm text-slate-600">
              {config.isEnabled 
                ? "Your chatbot is live and ready to help customers" 
                : "Turn on your chatbot to make it available to visitors"}
            </p>
          </div>
        </div>
        <Switch
          checked={config.isEnabled}
          onCheckedChange={onToggleEnabled}
          className="data-[state=checked]:bg-emerald-600 scale-110"
        />
      </div>
    </div>
  );
} 